// What the drawer's list does to the report's own columns, as flat properties.
// Always against the loaded columns, never against the last apply.

import { fieldName, fieldValue, isTrackedField } from './track'

import type { HeapProperties } from '@app/adapters/heap'
import type { ColumnType, ColumnValues, EditorColumn } from './types'

export interface ColumnsDiff {
  properties: HeapProperties
  // One event's worth of properties per column an apply added, changed or
  // removed.
  changes: HeapProperties[]
}

const ADDED_COUNT_KEYS: Record<ColumnType, string> = {
  Period: 'columns_added_period',
  PeriodDifference: 'columns_added_difference',
  PeriodAnalytical: 'columns_added_analytical',
  ColumnSpacer: 'columns_added_spacer',
}

const changedFields = (before: ColumnValues, after: ColumnValues): string[] =>
  [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => before[key] !== after[key]
  )

// Only the columns both lists hold: an add or a remove shifts every column after
// it, which is not a reorder.
const sharedOrder = (columns: EditorColumn[], keep: Set<number>): number[] =>
  columns
    .map((column) => column.id)
    .filter((id): id is number => id !== null && keep.has(id))

// Heap property names are global across every event in the project, so a form
// field key says nothing without a prefix: `field_` is only ever one of those,
// and `column_` is only ever our own metadata.
const prefixed = (
  prefix: string,
  type: ColumnType,
  values: Record<string, string | null>
): HeapProperties =>
  Object.fromEntries(
    Object.entries(values)
      .filter(([key]) => isTrackedField(key))
      .map(([key, value]) => [
        `${prefix}${fieldName(type, key)}`,
        fieldValue(value),
      ])
  )

// A column with no before-and-after to report: an added one had no previous
// values, a removed one has no new ones. `field_*` is the whole of it either
// way, so neither carries `field_was_*` or `column_fields_changed`.
//
// A removed column is described as the report had it. One added in the drawer
// and removed again is in neither list, which is right - the report never had
// it, so nothing about it changed.
const wholeColumnProperties = (
  change: 'added' | 'removed',
  column: EditorColumn
): HeapProperties => ({
  column_change: change,
  column_type: column.type,
  ...prefixed('field_', column.type, column.values),
})

// Where an edited column ended up, which fields moved, and what they held
// before. Without the before values an updated column reports the same
// properties as an untouched one, and the change is only recoverable by
// replaying every edit.
const updatedProperties = (
  column: EditorColumn,
  before: EditorColumn,
  changed: string[]
): HeapProperties => {
  return {
    column_change: 'updated',
    column_type: column.type,
    // Sorted by the tracked name, not the attribute it came from.
    column_fields_changed: changed
      .map((key) => fieldName(column.type, key))
      .sort()
      .join(','),
    ...prefixed('field_', column.type, column.values),
    ...prefixed(
      'field_was_',
      column.type,
      Object.fromEntries(
        changed.map((key) => [key, before.values[key] ?? null])
      )
    ),
  }
}

// Identifies a configuration across sessions. Not a checksum: a collision costs
// one miscounted repeat, and nothing else reads it.
function configDigest(columns: EditorColumn[]): string {
  const canonical = JSON.stringify(
    columns.map((column) => [
      column.type,
      Object.keys(column.values)
        .sort()
        .map((key) => [key, column.values[key]]),
    ])
  )

  let hash = 0x811c9dc5

  for (let index = 0; index < canonical.length; index += 1) {
    hash ^= canonical.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }

  return (hash >>> 0).toString(36)
}

// Columns are matched by server id, so one removed and added back again counts
// as both — the report treats it as a new column too.
export function columnsDiff(
  loaded: EditorColumn[],
  current: EditorColumn[]
): ColumnsDiff {
  const loadedById = new Map<number, EditorColumn>(
    loaded
      .filter(
        (column): column is EditorColumn & { id: number } => column.id !== null
      )
      .map((column) => [column.id, column])
  )
  const currentIds = new Set(
    current.map((column) => column.id).filter((id): id is number => id !== null)
  )
  const loadedIds = new Set(loadedById.keys())

  const added = current.filter((column) => column.id === null)
  const removed = loaded.filter(
    (column) => column.id !== null && !currentIds.has(column.id)
  )
  // Paired here once, so no consumer has to look the column back up.
  const updated = current.flatMap((column) => {
    const before = column.id === null ? undefined : loadedById.get(column.id)
    if (!before) return []

    const changed = changedFields(before.values, column.values)
    if (changed.length === 0) return []

    return [{ column, before, changed }]
  })

  const fieldsChanged = [
    ...new Set(
      updated.flatMap(({ column, changed }) =>
        changed.map((key) => fieldName(column.type, key))
      )
    ),
  ].sort()

  const before = sharedOrder(loaded, currentIds)
  const after = sharedOrder(current, loadedIds)

  const addedCounts = Object.fromEntries(
    Object.values(ADDED_COUNT_KEYS).map((key) => [key, 0])
  )
  added.forEach((column) => {
    addedCounts[ADDED_COUNT_KEYS[column.type]] += 1
  })

  return {
    properties: {
      columns_added: added.length,
      columns_updated: updated.length,
      columns_removed: removed.length,
      columns_reordered: before.some((id, index) => id !== after[index]),
      ...addedCounts,
      columns_total: current.length,
      columns_fields_changed: fieldsChanged.join(','),
      config_digest: configDigest(current),
    },
    changes: [
      ...added.map((column) => wholeColumnProperties('added', column)),
      ...updated.map(({ column, before, changed }) =>
        updatedProperties(column, before, changed)
      ),
      ...removed.map((column) => wholeColumnProperties('removed', column)),
    ],
  }
}
